import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  collection,
  getDoc,
  doc,
  addDoc,
  serverTimestamp,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useLocation, useParams } from "react-router-dom";
import { ReactFormGenerator, Registry } from "react-form-builder2";
import "./answerRigcheck.scss";
import { RotatingLines } from "react-loader-spinner";
import "react-form-builder2/dist/app.css";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PassFailComponent } from "./passFailComponent";
import { CommentComponent } from "./commentComponent";
import { SealComponent } from "./sealComponent";
import { ReplaceQuestionComponent } from "./replaceQuestionComponent";
import { SealedBrokenComponent } from "./sealedBrokenComponent";
import { SealNumberComponent } from "./sealNumberComponent";
import { NewSealNumberComponent } from "./newSealNumberComponent";
import { ExpirationDateComponent } from "./expirationDateComponent";
import "./expirationDateComponent.scss";
import Modal from "react-modal";
import CodeModal from "../../components/codeModal/codeModal";
import { applyAnswerPlaceholders } from "./applyAnswerPlaceholders";

// Helper function to format dates as "Apr 09th, 2025"
const formatDateString = (dateString) => {
  if (!dateString || dateString.trim() === "") return "";

  try {
    // Check if the input is a valid date string format (YYYY-MM-DD)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      console.warn(`Invalid date format: ${dateString}, returning as is`);
      return dateString; // Return the original value if not in expected format
    }

    // Parse the date parts manually to avoid timezone issues
    const [yearPart, monthPart, dayPart] = dateString
      .split("-")
      .map((num) => parseInt(num, 10));

    // Create date at noon (12:00 PM) to avoid timezone issues
    const date = new Date(yearPart, monthPart - 1, dayPart, 12, 0, 0);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date: ${dateString}, returning as is`);
      return dateString; // Return original if invalid date
    }

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Ensure we have valid month index
    const monthIndex = date.getMonth();
    if (monthIndex < 0 || monthIndex >= 12) {
      console.warn(
        `Invalid month index ${monthIndex} for date: ${dateString}, returning as is`
      );
      return dateString;
    }

    const monthName = months[monthIndex];
    const dayOfMonth = date.getDate();
    const fullYear = date.getFullYear();

    // Ensure day and year are valid
    if (
      dayOfMonth < 1 ||
      dayOfMonth > 31 ||
      fullYear < 1000 ||
      fullYear > 9999
    ) {
      console.warn(
        `Invalid day or year in date: ${dateString}, returning as is`
      );
      return dateString;
    }

    // Add appropriate suffix
    let suffix = "th";
    if (dayOfMonth === 1 || dayOfMonth === 21 || dayOfMonth === 31)
      suffix = "st";
    else if (dayOfMonth === 2 || dayOfMonth === 22) suffix = "nd";
    else if (dayOfMonth === 3 || dayOfMonth === 23) suffix = "rd";

    return `${monthName} ${dayOfMonth}${suffix}, ${fullYear}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    // Return the original value if any error occurs
    return dateString || "";
  }
};

const AnswerRigcheck = () => {
  const [checkData, setCheckData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showingPin, setShowingPin] = useState(false);
  const [userId, setUid] = useState("");
  const [draftData, setDraftData] = useState(null);
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const { agency } = useParams();
  const containerRef = useRef(null);
  const { checkId } = useParams();
  const formGeneratorRef = useRef(null);
  const [storedFormattedDate, setStoredFormattedDate] = useState(null);
  const saveTimeoutRef = useRef(null);
  const [draftStatus, setDraftStatus] = useState("idle"); // "idle" | "saving" | "saved" | "error"
  const [lastSavedTime, setLastSavedTime] = useState(null);
  const [showNoSaveWarning, setShowNoSaveWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleRadioChange = () => {
    const sealedRadio = containerRef.current.querySelectorAll(
      'input[type="radio"][value="sealed"]'
    );
    const brokenRadio = containerRef.current.querySelectorAll(
      'input[type="radio"][value="broken"]'
    );
    const noRadio = containerRef.current.querySelectorAll(
      'input[type="radio"][value="no"]'
    );
    const yesRadio = containerRef.current.querySelectorAll(
      'input[type="radio"][value="yes"]'
    );

    sealedRadio.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        if (e.target.checked) {
          const replaceDiv = radio
            .closest(".SortableItem")
            .nextElementSibling.querySelector(".replace");
          replaceDiv.style.display = "none";
        }

        // if one sibling up is has a seal-number-input and the value is empty, change the display of the .new-seal-number div from two sibings down
        const sealNumber = radio
          .closest(".SortableItem")
          .previousElementSibling.querySelector(".seal-number");
        if (sealNumber.querySelector("input").value === "") {
          const replaceQuestionSortableItem =
            radio.closest(".SortableItem").nextElementSibling;
          const newSealNumberDiv = replaceQuestionSortableItem
            .closest(".SortableItem")
            .nextElementSibling.querySelector(".new-seal-number");
          newSealNumberDiv.style.display = "block";
        }
      });
    });

    brokenRadio.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        if (e.target.checked) {
          const replaceDiv = radio
            .closest(".SortableItem")
            .nextElementSibling.querySelector(".replace");
          replaceDiv.style.display = "block";
        }

        // if one sibling up is has a seal-number-input and the value is empty, hide the .new-seal-number div from two sibings down
        // Unless yes is checked in the replace question.
        const sealNumber = radio
          .closest(".SortableItem")
          .previousElementSibling.querySelector(".seal-number");
        if (sealNumber.querySelector("input").value === "") {
          const replaceQuestionSortableItem =
            radio.closest(".SortableItem").nextElementSibling;
          const replaceQuestionRadio =
            replaceQuestionSortableItem.querySelector(
              'input[type="radio"][value="yes"]'
            );
          if (replaceQuestionRadio.checked) {
          } else {
            const newSealNumberDiv = replaceQuestionSortableItem
              .closest(".SortableItem")
              .nextElementSibling.querySelector(".new-seal-number");
            newSealNumberDiv.style.display = "none";
          }
        }
      });
    });

    noRadio.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        if (e.target.checked) {
          const replaceDiv = radio
            .closest(".SortableItem")
            .nextElementSibling.querySelector(".new-seal-number");
          replaceDiv.style.display = "none";
        }
      });
    });

    yesRadio.forEach((radio) => {
      radio.addEventListener("change", (e) => {
        if (e.target.checked) {
          const replaceDiv = radio
            .closest(".SortableItem")
            .nextElementSibling.querySelector(".new-seal-number");
          replaceDiv.style.display = "block";
        }
      });
    });
  };
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      uid: params.get("uid"),
      // Add more as needed
    };
  };
  useEffect(() => {
    const queryParams = getQueryParams();
    console.log("queryParams", queryParams);
    // Perform actions based on query params
    if (queryParams.uid) {
      setUid(queryParams.uid);
      checkForDraft(queryParams.uid);
      getDoc(doc(db, `users/${queryParams.uid}`)).then((doc) => {
        if (doc.exists()) {
          if (!doc.data().disabled) {
            setShowingPin(false);
          }
        }
      });
      // Logic based on param1
    }
  }, [location.search]);

  useEffect(() => {
    if (!loading) {
      // query all elements with class seal-number-input
      const sealNumberInputs = document.querySelectorAll(".seal-number-input");
      const sealNumbers = checkData.sealNumbers;
      // loop through all elements with class seal-number-input and set the value to the corresponding value in sealNumbers. Its corresponding is based on index.
      if (sealNumbers && sealNumbers.length) {
        sealNumberInputs.forEach((input, index) => {
          input.value = sealNumbers[index]
            ? sealNumbers[index].value || ""
            : "";
        });
      } else {
        sealNumberInputs.forEach((input) => {
          input.value = "";
        });
      }

      handleRadioChange();
    }
  }, [loading, checkData]);

  // Add expiration date input for checkbox options with hasExpiration property
  useEffect(() => {
    if (!loading && checkData.form) {
      try {
        const formData = JSON.parse(checkData.form);

        // Find all checkbox options that have hasExpiration set to true
        const checkboxesWithExpiration = formData.filter(
          (field) =>
            field.element === "Checkboxes" &&
            field.options &&
            field.options.some((option) => option.hasExpiration === true)
        );

        // For each checkbox field with hasExpiration options
        setTimeout(() => {
          checkboxesWithExpiration.forEach((field) => {
            field.options.forEach((option, index) => {
              const fieldContainer = document
                .querySelector(`[name^="option_${option.key}"]`)
                ?.closest(".form-group");
              if (fieldContainer) {
                // Find which options have hasExpiration
                if (option.hasExpiration === true) {
                  // Find the specific checkbox option
                  const optionElement = fieldContainer.querySelectorAll(
                    ".custom-control.custom-checkbox"
                  )[index];

                  if (optionElement) {
                    // Create a container for the ExpirationDateComponent
                    const expirationContainer = document.createElement("div");
                    expirationContainer.className = "modern-date-input";

                    // Get the checkbox option label/text
                    const checkboxLabel = optionElement.querySelector("label");
                    const optionText = checkboxLabel
                      ? checkboxLabel.textContent.trim()
                      : "";
                    const labelText = optionText
                      ? `${optionText} Expiration Date`
                      : "Expiration Date";

                    // Create a label with the proper styling
                    const expirationLabel = document.createElement("label");
                    expirationLabel.className = "date-label";
                    const labelSpan = document.createElement("span");
                    labelSpan.textContent = labelText;
                    expirationLabel.appendChild(labelSpan);

                    // Create the input wrapper
                    const inputWrapper = document.createElement("div");
                    inputWrapper.className = "date-input-wrapper form-group";

                    // Create the expiration date input
                    const expirationInput = document.createElement("input");
                    expirationInput.type = "date";
                    expirationInput.className = "modern-date-control";
                    expirationInput.name = `${option.key}_expiration`;

                    // Format the expiration date properly
                    if (option.expiration_date) {
                      // If the date is already in YYYY-MM-DD format, use it directly
                      if (/^\d{4}-\d{2}-\d{2}$/.test(option.expiration_date)) {
                        expirationInput.value = option.expiration_date;
                      } else {
                        // Parse the formatted date (e.g., "Feb 22nd, 2222")
                        try {
                          // Extract the parts from the formatted date
                          const dateParts = option.expiration_date.match(
                            /([A-Za-z]{3})\s+(\d{1,2})(?:st|nd|rd|th),\s+(\d{4})/
                          );

                          if (dateParts) {
                            const monthName = dateParts[1];
                            const day = parseInt(dateParts[2], 10);
                            const year = parseInt(dateParts[3], 10);

                            // Convert month name to month number (0-11)
                            const months = [
                              "Jan",
                              "Feb",
                              "Mar",
                              "Apr",
                              "May",
                              "Jun",
                              "Jul",
                              "Aug",
                              "Sep",
                              "Oct",
                              "Nov",
                              "Dec",
                            ];
                            const monthIndex = months.findIndex(
                              (m) => m === monthName
                            );

                            if (
                              monthIndex !== -1 &&
                              !isNaN(day) &&
                              !isNaN(year)
                            ) {
                              // Create date and format as YYYY-MM-DD
                              const date = new Date(
                                year,
                                monthIndex,
                                day,
                                12,
                                0,
                                0
                              );
                              if (!isNaN(date.getTime())) {
                                const formattedDate = date
                                  .toISOString()
                                  .split("T")[0];
                                expirationInput.value = formattedDate;
                              } else {
                                console.warn(
                                  "Invalid date created from:",
                                  option.expiration_date
                                );
                                expirationInput.value = "";
                              }
                            } else {
                              console.warn(
                                "Could not parse date parts correctly:",
                                option.expiration_date
                              );
                              expirationInput.value = "";
                            }
                          } else {
                            // If we can't parse it with the regex, try direct Date parsing
                            const date = new Date(option.expiration_date);
                            if (!isNaN(date.getTime())) {
                              expirationInput.value = date
                                .toISOString()
                                .split("T")[0];
                            } else {
                              console.warn(
                                "Could not parse date format:",
                                option.expiration_date
                              );
                              expirationInput.value = "";
                            }
                          }
                        } catch (error) {
                          console.error("Error parsing date:", error);
                          expirationInput.value = "";
                        }
                      }
                    } else {
                      expirationInput.value = "";
                    }

                    // Create the date icon element
                    const dateIcon = document.createElement("div");
                    dateIcon.className = "date-icon";
                    dateIcon.textContent = "📅";

                    // Append elements in the correct structure
                    expirationContainer.appendChild(expirationLabel);
                    inputWrapper.appendChild(expirationInput);
                    inputWrapper.appendChild(dateIcon);
                    expirationContainer.appendChild(inputWrapper);
                    optionElement.appendChild(expirationContainer);
                  }
                }
              }
            });
          });
        }, 500); // Small delay to ensure DOM is fully rendered
      } catch (error) {
        console.error("Error processing form data for hasExpiration:", error);
      }
    }
  }, [loading, checkData]);

  const handlePinSubmit = async (data) => {
    let pin = data.join("");

    let agencySettingsDoc = await getDoc(
      doc(db, `Agencies/${agency}/Settings/PIN`)
    );

    if (agencySettingsDoc.exists()) {
      let agencyPin = agencySettingsDoc.data().value;

      if (pin === agencyPin) {
        setShowingPin(false);
      } else {
        toast.error("Invalid PIN", {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
      }
    }
  };
  const handleSubmit = async (data) => {
    if (showingPin) return;
    // handle validation for new seal number
    const sortableItems = document.querySelectorAll(".SortableItem");

    for (let i = 0; i < sortableItems.length; i++) {
      const item = sortableItems[i];
      const inputs = item.querySelectorAll("input");
      const nextItem = sortableItems[i + 1];
      if (nextItem) {
        const sealNumberInput = nextItem.querySelector(
          ".new-seal-number-input"
        );
        if (
          inputs.length &&
          inputs[0].value === "yes" &&
          inputs[0].checked &&
          sealNumberInput &&
          !sealNumberInput.value
        ) {
          toast.warn("Please enter a new seal number", {
            position: "top-center",
            autoClose: 4000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
          });
          return;
        }
      }
    }

    setIsSubmitting(true);
    const colRef = collection(
      db,
      `RigcheckAnswers/OdtRgRZbYmEnNipNIft7/${agency} Rigchecks Answers`
    );

    // Process the data to include expiration dates
    const processedData = data.map((item) => {
      if (item.name.includes("sealedBroken")) {
        let expirationDates = {};
        const expirationInputs = document.querySelectorAll(
          'input[name*="_expiration"]'
        );

        const formData = JSON.parse(checkData.form);
        console.log("formData", formData);
        const sealedBrokenFields = formData.filter(
          (field) => field.name === "SealedBroken"
        );
        console.log("sealedBrokenFields", sealedBrokenFields);
        sealedBrokenFields.forEach((field) => {
          if (field.options) {
            field.options.forEach((option) => {
              console.log("option", option);
              if (option.hasExpiration) {
                expirationInputs.forEach((input) => {
                  if (input.name.includes(option.key)) {
                    // Key by option.key (unique). Templates with duplicate
                    // option.values used to collapse here and overwrite each
                    // other's dates before the placeholder write.
                    expirationDates[option.key] = formatDateString(
                      input.value
                    );
                  }
                });
              }
            });
          }
        });
        // find the expiration date inputs
        return {
          ...item,
          expiration_dates: expirationDates,
        };
      }
      // Find the radio input element (try checked first, then any with that name)
      let radioInput = document.querySelector(`input[name="${item.name}"]:checked`);
      if (!radioInput) {
        radioInput = document.querySelector(`input[name="${item.name}"][type="radio"]`);
      }
      if (radioInput) {
        // Find the parent form-group div
        const formGroup = radioInput.closest(".form-group");
        if (formGroup) {
          // Check if this field has requiresExpirationPerUnit by looking for the hidden input
          const unitExpirationsInput = formGroup.querySelector(`input[name="${item.name}_unitExpirations"]`);
          const hasUnitExpirations = unitExpirationsInput !== null;
          
          if (hasUnitExpirations) {
            // For per-unit expiration dates, we need to ensure the unitExpirations array
            // in the hidden input contains dates in YYYY-MM-DD format (which the backend can parse)
            // The date inputs are already in YYYY-MM-DD format, so we'll update the hidden input
            
            // Get all unit expiration date inputs and update the hidden input array
            const allDateInputs = formGroup.querySelectorAll('input[type="date"]');
            const unitExpirationArray = [];
            
            // Collect dates from date inputs (they're in YYYY-MM-DD format)
            const dateInputMap = new Map();
            allDateInputs.forEach((input) => {
              if (input.name.includes("_unit_") && input.name.includes("_expiration") && input.value) {
                const match = input.name.match(/_unit_(\d+)_expiration/);
                if (match) {
                  const unitIndex = parseInt(match[1], 10);
                  dateInputMap.set(unitIndex, input.value); // Already in YYYY-MM-DD format
                }
              }
            });
            
            // Get currentQuantity to know array size
            const currentQuantityInput = formGroup.querySelector(`input[name="${item.name}_currentQuantity"]`);
            const currentQuantity = currentQuantityInput ? parseInt(currentQuantityInput.value || "0", 10) : 0;
            
            // Build the array with dates in YYYY-MM-DD format
            // Prioritize date inputs (most reliable), fallback to hidden input
            for (let i = 0; i < currentQuantity; i++) {
              if (dateInputMap.has(i)) {
                // Date inputs are already in YYYY-MM-DD format
                unitExpirationArray.push(dateInputMap.get(i));
              } else {
                // Fallback to hidden input if date input doesn't have value
                try {
                  const existingExpirations = unitExpirationsInput.value ? JSON.parse(unitExpirationsInput.value) : [];
                  if (Array.isArray(existingExpirations) && existingExpirations[i]) {
                    const existingDate = existingExpirations[i];
                    // If already in YYYY-MM-DD format, use it
                    if (/^\d{4}-\d{2}-\d{2}$/.test(existingDate)) {
                      unitExpirationArray.push(existingDate);
                    } else {
                      // Try to parse and convert to YYYY-MM-DD
                      const date = new Date(existingDate);
                      if (!isNaN(date.getTime())) {
                        unitExpirationArray.push(date.toISOString().split("T")[0]);
                      } else {
                        unitExpirationArray.push("");
                      }
                    }
                  } else {
                    unitExpirationArray.push("");
                  }
                } catch (e) {
                  unitExpirationArray.push("");
                }
              }
            }
            
            // Update the hidden input with the properly formatted array
            if (unitExpirationsInput) {
              unitExpirationsInput.value = JSON.stringify(unitExpirationArray);
            }
            
            // Also get the overall expiration date if it exists (not a unit expiration)
            const overallDateInput = formGroup.querySelector(`input[name="${item.name}_expiration"][type="date"]`);
            const result = {
              ...item,
              value: radioInput.value,
            };
            
            // Store formatted dates for display purposes (if needed elsewhere)
            const formattedUnitExpirationDates = {};
            unitExpirationArray.forEach((date, index) => {
              if (date && date.trim() !== "") {
                formattedUnitExpirationDates[`unit_${index}`] = formatDateString(date);
              }
            });
            
            if (Object.keys(formattedUnitExpirationDates).length > 0) {
              result.expiration_dates = formattedUnitExpirationDates;
            }
            
            if (overallDateInput && overallDateInput.value) {
              result.expiration_date = formatDateString(overallDateInput.value);
            }
            
            return result;
          } else {
            // Look for the single date input within this form group (overall expiration)
            // Make sure it's not a unit expiration date
            const overallDateInput = formGroup.querySelector(`input[name="${item.name}_expiration"][type="date"]`);
            
            if (overallDateInput && overallDateInput.value) {
              return {
                ...item,
                value: radioInput.value,
                expiration_date: formatDateString(overallDateInput.value),
              };
            }
          }
        }
      }
      if (item.name.includes("checkboxes")) {
        // this is a checkboxes component, so grab the expiration dates
        // Grab all inputs that contain the string _expiration.
        const expirationInputs = document.querySelectorAll(
          'input[name*="_expiration"]'
        );
        let expirationDatesInputs = {};
        expirationInputs.forEach((input) => {
          // Extract the base name by removing "_expiration" from the input name
          const baseName = input.name.replace("_expiration", "");
          expirationDatesInputs[baseName] = formatDateString(input.value);
        });

        return {
          ...item,
          expiration_dates: expirationDatesInputs,
        };
      }
      return item;
    });

    // Add hidden inputs for unitExpirations and currentQuantity to the submission data
    // These are needed by the Firebase function to process per-unit expiration dates
    const unitExpirationsInputs = document.querySelectorAll('input[name*="_unitExpirations"]');
    const currentQuantityInputs = document.querySelectorAll('input[name*="_currentQuantity"]');
    
    unitExpirationsInputs.forEach((input) => {
      // Add as a separate item in the array - Firebase function looks for these by name
      const item = {
        name: input.name,
        value: input.value || "[]",
        id: `${input.name}_id`,
        custom_name: input.name,
      };
      console.log(`Adding unitExpirations item:`, item);
      processedData.push(item);
    });
    
    currentQuantityInputs.forEach((input) => {
      // Add as a separate item in the array - Firebase function looks for these by name
      const item = {
        name: input.name,
        value: input.value || "0",
        id: `${input.name}_id`,
        custom_name: input.name,
      };
      console.log(`Adding currentQuantity item:`, item);
      processedData.push(item);
    });
    
    console.log(`Total items in processedData:`, processedData.length);
    console.log(`Items with _unitExpirations or _currentQuantity:`, 
      processedData.filter(item => item.name.includes("_unitExpirations") || item.name.includes("_currentQuantity"))
    );

    try {
      const submissionData = {
        answerData: processedData,
        createdAt: serverTimestamp(),
        rigcheckName: checkData.name,
        rigcheckId: checkId,
        uid: userId,
      };

      // Include parentCheckId if this is a child check
      if (checkData.parentCheckId) {
        submissionData.parentCheckId = checkData.parentCheckId;
      }

      const docRef = await addDoc(colRef, submissionData);

      // Write the new expiration-date placeholders back into the rigcheck
      // template so the next user opens the form and sees the dates we just
      // submitted. The cloud trigger no longer does this (it would collide
      // on duplicate option.values); the client owns the per-option keys.
      try {
        await applyAnswerPlaceholders({
          agency,
          checkId,
          processedData,
        });
      } catch (e) {
        console.log("Error updating rigcheck placeholders:", e);
      }

      // After successful submission, delete any existing draft
      if (userId) {
        const draftRef = doc(db, "RigcheckDrafts", checkId, "drafts", userId);
        await deleteDoc(draftRef);
      }
      toast.success("Form Saved Successfully", {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      navigate(`/${agency}`);
    } catch (err) {
      toast.warn("Error Saving Form", {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkForDraft = async (uid) => {
    console.log("checking for draft");
    console.log("uid", uid);
    if (!uid) return;

    try {
      const draftRef = doc(db, "RigcheckDrafts", checkId, "drafts", uid);
      const draftSnap = await getDoc(draftRef);
      if (draftSnap.exists() && draftSnap.data().rigcheckId === checkId) {
        setDraftData(draftSnap.data());
        setShowDraftPrompt(true);
      }
    } catch (error) {
      console.error("Error checking for draft:", error);
    }
  };

  // Apply initial visibility based on current checked state of radio buttons
  // This is needed when restoring from a draft since handleRadioChange only adds listeners
  const applyInitialRadioState = () => {
    if (!containerRef.current) return;

    // Check "yes" radio buttons (replace seal) - show new seal number input
    const yesRadios = containerRef.current.querySelectorAll(
      'input[type="radio"][value="yes"]:checked'
    );
    yesRadios.forEach((radio) => {
      const newSealDiv = radio
        .closest(".SortableItem")
        ?.nextElementSibling?.querySelector(".new-seal-number");
      if (newSealDiv) {
        newSealDiv.style.display = "block";
      }
    });

    // Check "no" radio buttons - hide new seal number input
    const noRadios = containerRef.current.querySelectorAll(
      'input[type="radio"][value="no"]:checked'
    );
    noRadios.forEach((radio) => {
      const newSealDiv = radio
        .closest(".SortableItem")
        ?.nextElementSibling?.querySelector(".new-seal-number");
      if (newSealDiv) {
        newSealDiv.style.display = "none";
      }
    });

    // Check "sealed" radio buttons - hide replace question
    const sealedRadios = containerRef.current.querySelectorAll(
      'input[type="radio"][value="sealed"]:checked'
    );
    sealedRadios.forEach((radio) => {
      const replaceDiv = radio
        .closest(".SortableItem")
        ?.nextElementSibling?.querySelector(".replace");
      if (replaceDiv) {
        replaceDiv.style.display = "none";
      }
      // Show new seal number if seal number is empty
      const sealNumber = radio
        .closest(".SortableItem")
        ?.previousElementSibling?.querySelector(".seal-number");
      if (sealNumber?.querySelector("input")?.value === "") {
        const newSealNumberDiv = radio
          .closest(".SortableItem")
          ?.nextElementSibling?.nextElementSibling?.querySelector(".new-seal-number");
        if (newSealNumberDiv) {
          newSealNumberDiv.style.display = "block";
        }
      }
    });

    // Check "broken" radio buttons - show replace question
    const brokenRadios = containerRef.current.querySelectorAll(
      'input[type="radio"][value="broken"]:checked'
    );
    brokenRadios.forEach((radio) => {
      const replaceDiv = radio
        .closest(".SortableItem")
        ?.nextElementSibling?.querySelector(".replace");
      if (replaceDiv) {
        replaceDiv.style.display = "block";
      }
    });
  };

  const handleDraftContinue = () => {
    // Force ReactFormGenerator to remount with the draft's answer_data
    // by incrementing the form key. This ensures the component reinitializes
    // with the draft data including seal numbers and expiration dates.
    setFormKey((prev) => prev + 1);
    setShowDraftPrompt(false);
    
    // Trigger radio change handlers and apply initial state after remount
    setTimeout(() => {
      handleRadioChange();
      applyInitialRadioState();
    }, 100);
  };

  const handleDraftDiscard = async () => {
    if (!userId) {
      console.error("Cannot discard draft: userId is empty");
      toast.error("Error discarding draft");
      setShowDraftPrompt(false);
      window.location.reload();
      return;
    }
    
    try {
      const draftRef = doc(db, "RigcheckDrafts", checkId, "drafts", userId);
      await deleteDoc(draftRef);
      setDraftData(null);
      setShowDraftPrompt(false);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting draft:", error);
      toast.error("Error deleting draft");
      setShowDraftPrompt(false);
    }
  };

  const saveDraft = async (data) => {
    if (!userId) return;

    setDraftStatus("saving");
    
    try {
      const draftRef = doc(db, "RigcheckDrafts", checkId, "drafts", userId);
      const draftData = {
        answerData: data,
        rigcheckId: checkId,
        rigcheckName: checkData.name,
        updatedAt: serverTimestamp(),
        uid: userId,
      };

      // Include parentCheckId if this is a child check
      if (checkData.parentCheckId) {
        draftData.parentCheckId = checkData.parentCheckId;
      }

      await setDoc(draftRef, draftData);
      setDraftStatus("saved");
      setLastSavedTime(new Date());
    } catch (error) {
      console.error("Error saving draft:", error);
      setDraftStatus("error");
      toast.error("Error saving draft");
    }
  };

  const debouncedSaveDraft = (data) => {
    console.log("data", data);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Show pending state while waiting for debounce
    setDraftStatus("saving");

    saveTimeoutRef.current = setTimeout(() => {
      // Enhance data with expiration dates from DOM inputs
      const enhancedData = data.map((item) => {
        // Special handling for sealedBroken items - capture checkbox expiration dates
        if (item.name && item.name.includes("sealedBroken")) {
          let expirationDates = {};
          const formData = JSON.parse(checkData.form);
          const sealedBrokenFields = formData.filter(
            (field) => field.name === "SealedBroken"
          );
          sealedBrokenFields.forEach((field) => {
            if (field.options) {
              field.options.forEach((option) => {
                if (option.hasExpiration) {
                  const expirationInput = document.querySelector(
                    `input[name="${option.key}_expiration"]`
                  );
                  if (expirationInput && expirationInput.value) {
                    // Store with option.key as the key (matches component state)
                    expirationDates[option.key] = expirationInput.value;
                  }
                }
              });
            }
          });
          
          // Parse existing value and merge with expiration dates
          let existingValue = {};
          if (item.value) {
            try {
              existingValue = typeof item.value === 'string' ? JSON.parse(item.value) : item.value;
            } catch (e) {
              existingValue = { label: item.value };
            }
          }
          
          return {
            ...item,
            value: JSON.stringify({
              ...existingValue,
              expirationDates: expirationDates,
            }),
          };
        }
        
        // Handle PassFailComponent with per-unit expirations (multiple lots)
        const unitExpirationsInput = document.querySelector(`input[name="${item.name}_unitExpirations"]`);
        const currentQuantityInput = document.querySelector(`input[name="${item.name}_currentQuantity"]`);
        if (unitExpirationsInput && unitExpirationsInput.value) {
          let enhancedItem = { ...item };
          try {
            const unitExpirations = JSON.parse(unitExpirationsInput.value);
            if (Array.isArray(unitExpirations) && unitExpirations.length > 0) {
              enhancedItem.unitExpirations = unitExpirations;
            }
          } catch (e) {
            console.error("Error parsing unitExpirations:", e);
          }
          if (currentQuantityInput && currentQuantityInput.value) {
            enhancedItem.currentQuantity = parseInt(currentQuantityInput.value, 10);
          }
          // Also check for overall expiration
          const expirationInput = document.querySelector(`input[name="${item.name}_expiration"][type="date"]`);
          if (expirationInput && expirationInput.value) {
            enhancedItem.expiration_date = expirationInput.value;
          }
          return enhancedItem;
        }
        
        // Look for expiration date input for this item
        const expirationInput = document.querySelector(`input[name="${item.name}_expiration"][type="date"]`);
        if (expirationInput && expirationInput.value) {
          return {
            ...item,
            expiration_date: expirationInput.value, // Store in YYYY-MM-DD format
          };
        }
        return item;
      });
      
      saveDraft(enhancedData);
    }, 1200); // 2 second delay
  };

  useEffect(() => {
    const fetchRigcheck = async () => {
      const docRef = doc(
        db,
        "Rigchecks",
        "VgTv80ik2BtavT0A5owc",
        agency + " Rigchecks",
        checkId
      );
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const docData = docSnap.data(); // If there's a form, parse it and look for expiration dates
        if (docData.form) {
          const formData = JSON.parse(docData.form);

          // Update all PassFailComponents that have expiration dates
          const updatedFormData = formData.map((field) => {
            if (
              field.key &&
              field.key.includes("PassFailComponent") &&
              field.expiration_date
            ) {
              try {
                // Format date as YYYY-MM-DD for HTML date input
                let date;

                // Check if the date is already in YYYY-MM-DD format
                if (/^\d{4}-\d{2}-\d{2}$/.test(field.expiration_date)) {
                  date = new Date(field.expiration_date);
                } else {
                  // Parse formatted date like "Sep 24th, 2025"
                  const dateParts = field.expiration_date.match(
                    /([A-Za-z]{3})\s+(\d{1,2})(?:st|nd|rd|th),\s+(\d{4})/
                  );

                  if (dateParts) {
                    const monthName = dateParts[1];
                    const day = parseInt(dateParts[2], 10);
                    const year = parseInt(dateParts[3], 10);

                    // Convert month name to month number (0-11)
                    const months = [
                      "Jan",
                      "Feb",
                      "Mar",
                      "Apr",
                      "May",
                      "Jun",
                      "Jul",
                      "Aug",
                      "Sep",
                      "Oct",
                      "Nov",
                      "Dec",
                    ];
                    const monthIndex = months.findIndex((m) => m === monthName);

                    if (monthIndex !== -1 && !isNaN(day) && !isNaN(year)) {
                      // Create date at noon to avoid timezone issues
                      date = new Date(year, monthIndex, day, 12, 0, 0);
                    } else {
                      console.warn(
                        `Could not parse date parts: ${field.expiration_date}` 
                      );
                      date = new Date(field.expiration_date); // Fallback to original parsing
                    }
                  } else {
                    // Try direct Date parsing as fallback
                    date = new Date(field.expiration_date);
                  }
                }

                // Check if date is valid before calling toISOString()
                if (!isNaN(date.getTime())) {
                  const formattedDate = date.toISOString().split("T")[0];
                  setStoredFormattedDate(formattedDate);
                  // Set the date in the field's props
                  if (!field.props) {
                    field.props = {};
                  }
                  field.value = field.value || "pass";
                  if (!field.name.includes("Quick Check")) {
                    field.value = formattedDate;
                  } else {
                    setTimeout(() => {
                      let field_name = field.field_name + "_expiration";
                      const elements = Array.from(
                        document.getElementsByName(field_name)
                      );
                      elements.forEach((element) => {
                        if (element) {
                          element.value = formattedDate;
                        }
                      });
                    }, 500);
                  }
                } else {
                  console.warn(`Invalid date found: ${field.expiration_date}`);
                  // Keep the original field.value if date is invalid
                }
              } catch (error) {
                console.error("Error formatting date:", error);
                // Keep original field.value if there's an error
              }
            }

            return field;
          });

          docData.form = JSON.stringify(updatedFormData);
        }

        setCheckData(docData);
      }
      setLoading(false);
    };
    fetchRigcheck();
  }, []);

  try {
    Registry.register("PassFailComponent", PassFailComponent);
    Registry.register("CommentComponent", CommentComponent);
    Registry.register("SealComponent", SealComponent);
    Registry.register("ReplaceQuestionComponent", ReplaceQuestionComponent);
    Registry.register("SealedBrokenComponent", SealedBrokenComponent);
    Registry.register("SealNumberComponent", SealNumberComponent);
    Registry.register("NewSealNumberComponent", NewSealNumberComponent);
    Registry.register("ExpirationDateComponent", ExpirationDateComponent);
  } catch (error) {}
  return (
    <div>
      <Helmet>
        {" "}
        <link
          rel="stylesheet"
          href="https://stackpath.bootstrapcdn.com/bootstrap/4.4.1/css/bootstrap.min.css"
        />{" "}
        <link
          rel="stylesheet"
          href="https://use.fontawesome.com/releases/v5.13.0/css/all.css"
        />{" "}
      </Helmet>{" "}
      {loading ? (
        <div
          style={{ display: "flex", justifyContent: "center", height: "100vh" }}
        >
          <RotatingLines
            strokeColor="grey"
            strokeWidth="5"
            animationDuration="0.75"
            width="50"
            visible={true}
          />
        </div>
      ) : (
        <div className="background-container">
          <Modal
            isOpen={showDraftPrompt}
            style={{
              overlay: {
                zIndex: 9999,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
              },
              content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "white",
                padding: "20px",
                borderRadius: "8px",
                maxWidth: "400px",
                width: "90%",
                zIndex: 10000,
              },
            }}
          >
            <h2>Continue Draft?</h2>
            <p>
              You have an existing draft for this rigcheck. Would you like to
              continue where you left off?
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: "20px",
              }}
            >
              <button
                type="button"
                onClick={handleDraftDiscard}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Discard Draft
              </button>
              <button
                type="button"
                onClick={handleDraftContinue}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Continue Draft
              </button>
            </div>
          </Modal>

          <div
            className="form-generator-container"
            ref={containerRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.type !== "textarea") {
                e.preventDefault();
              }
            }}
          >
            <div className="form-title">{checkData.name}</div>
            <div className="required-text">
              Fields marked with an <span style={{ color: "red" }}>*</span> are
              required
            </div>
            {/* Draft Status Indicator - Fixed position for mobile scrolling */}
            {userId && draftStatus !== "idle" && (
              <div
                style={{
                  position: "fixed",
                  bottom: "20px",
                  left: "20px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  background: draftStatus === "saving" 
                    ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                    : draftStatus === "saved"
                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                    : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  borderRadius: "24px",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "white",
                  boxShadow: draftStatus === "saving"
                    ? "0 4px 12px rgba(245, 158, 11, 0.4)"
                    : draftStatus === "saved"
                    ? "0 4px 12px rgba(16, 185, 129, 0.4)"
                    : "0 4px 12px rgba(239, 68, 68, 0.4)",
                  zIndex: 1000,
                  whiteSpace: "nowrap",
                }}
              >
                {draftStatus === "saving" && (
                  <>
                    <span
                      style={{
                        width: "14px",
                        height: "14px",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "white",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    <span>Saving draft...</span>
                  </>
                )}
                {draftStatus === "saved" && (
                  <>
                    <span style={{ fontSize: "14px" }}>✓</span>
                    <span>
                      Draft saved
                      {lastSavedTime && (
                        <span style={{ opacity: 0.85, marginLeft: "6px" }}>
                          {lastSavedTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </span>
                  </>
                )}
                {draftStatus === "error" && (
                  <>
                    <span style={{ fontSize: "14px" }}>✕</span>
                    <span>Save failed</span>
                  </>
                )}
              </div>
            )}
            {/* No Auto-Save Warning - shown when no userId */}
            {!userId && (
              <>
                <div
                  onClick={() => setShowNoSaveWarning(!showNoSaveWarning)}
                  style={{
                    position: "fixed",
                    bottom: "20px",
                    left: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "40px",
                    height: "40px",
                    backgroundColor: "#fef3c7",
                    borderRadius: "50%",
                    fontSize: "20px",
                    border: "2px solid #f59e0b",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.15)",
                    zIndex: 1000,
                    cursor: "pointer",
                  }}
                >
                  ⚠️
                </div>
                {showNoSaveWarning && (
                  <div
                    style={{
                      position: "fixed",
                      bottom: "70px",
                      left: "20px",
                      maxWidth: "280px",
                      padding: "12px 16px",
                      backgroundColor: "#fffbeb",
                      borderRadius: "8px",
                      fontSize: "13px",
                      color: "#92400e",
                      border: "1px solid #fcd34d",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
                      zIndex: 1001,
                    }}
                  >
                    <div style={{ fontWeight: "600", marginBottom: "6px" }}>
                      Auto-Save Unavailable
                    </div>
                    <div style={{ lineHeight: "1.4" }}>
                      Your progress cannot be saved automatically. Closing or refreshing this page before submitting will result in data loss.
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNoSaveWarning(false)}
                      style={{
                        marginTop: "10px",
                        padding: "6px 12px",
                        backgroundColor: "#f59e0b",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      Got it
                    </button>
                  </div>
                )}
              </>
            )}
            <style>
              {`
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.4; }
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <ReactFormGenerator
              key={formKey}
              ref={formGeneratorRef}
              data={(() => {
                const formData = JSON.parse(checkData.form);
                // If we have draft data, merge expiration dates and unit expirations into form field data
                if (draftData && draftData.answerData) {
                  return formData.map((field) => {
                    const draftItem = draftData.answerData.find(
                      (item) => item.name === field.field_name
                    );
                    if (draftItem) {
                      let mergedField = { ...field };
                      // Merge expiration_date if present
                      if (draftItem.expiration_date) {
                        mergedField.expiration_date = draftItem.expiration_date;
                      }
                      // Merge unitExpirations for multi-lot items
                      if (draftItem.unitExpirations) {
                        mergedField.unitExpirations = draftItem.unitExpirations;
                      }
                      // Merge currentQuantity for multi-lot items
                      if (draftItem.currentQuantity !== undefined) {
                        mergedField.currentQuantity = draftItem.currentQuantity;
                      }
                      return mergedField;
                    }
                    return field;
                  });
                }
                return formData;
              })()}
              answer_data={draftData ? draftData.answerData : []}
              back_action={`/${agency}`}
              back_name="Back"
              onSubmit={handleSubmit}
              onChange={(data) => debouncedSaveDraft(data)}
              submitButton={
                <button type="submit" className="btn" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              }
            />
          </div>
          <Modal
            isOpen={showingPin}
            style={{
              content: {
                backgroundColor: "transparent",
                border: "none",
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
              },
            }}
            contentLabel="Example Modal"
          >
            <CodeModal submit={handlePinSubmit} />
          </Modal>
        </div>
      )}
    </div>
  );
};

export default AnswerRigcheck;
